import React, {useEffect, useState} from "react";
import './CustomerEvaluation.css'
import SignatureComponent from "../SignaturePadComponent/SignatureComponent";
import {useSendFinishedCustomerEvaluation} from "../../hooks/Technician/useSendFinishedCustomerEvaluation";
import {Bounce, toast} from "react-toastify";
import UploadTicketImages from "../UploadTicketImages/UploadTicketImages";

interface CustomerEvaluationProps {
    onBack: () => void;
    ticketUniqueCode: string;
    updateTicketStateLocally: (newState: any) => void;
    updateTicketState: (newState: any, ticketUniqueCode: string) => void;
}

const MAX_TOTAL_SIZE_MB = 50; // Maximum allowed size for total images (in MB)

export const CustomerEvaluation: React.FC<CustomerEvaluationProps> = ({
                                                                          onBack,
                                                                          ticketUniqueCode,
                                                                          updateTicketStateLocally,
                                                                          updateTicketState
                                                                      }) => {

    const [serviceSolved, setServiceSolved] = useState("");
    const [serviceSolvedDescription, setServiceSolvedDescription] = useState("");
    const [clearArea, setClearArea] = useState("");
    const [clearAreaDescription, setClearAreaDescription] = useState("");
    const [questionsAnswered, setQuestionsAnswered] = useState("");
    const [questionsAnsweredDescription, setQuestionsAnsweredDescription] = useState("");
    const [technicianAttitude, setTechnicianAttitude] = useState("");
    const [additionalComment, setAdditionalComment] = useState("");
    const [saveEvaluationDisabled, setSaveEvaluationDisabled] = useState(true);
    const [completeEvaluationModalVisible, setCompleteEvaluationModalVisible] = useState(false);
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [companySignatureImage, setCompanySignatureImage] = useState<string | null>(null);
    const [technicianSignatureImage, setTechnicianSignatureImage] = useState<string | null>(null);
    const [supervisorName, setSupervisorName] = useState("");
    const [supervisorEmail, setSupervisorEmail] = useState("");
    const [supervisorPhone, setSupervisorPhone] = useState("");
    const [supervisorPosition, setSupervisorPosition] = useState("");
    const [sendingEvaluation, setSendingEvaluation] = useState(false);
    const [showAttachImages, setShowAttachImages] = useState(true);
    const [selectedImages, setSelectedImages] = useState<any>([]);
    const {sendCustomerEvaluation} = useSendFinishedCustomerEvaluation(setSendingEvaluation, setShowSignatureModal, onBack)

    useEffect(() => {
        const isDisabled = !serviceSolved || !clearArea || !questionsAnswered || !technicianAttitude ||
            !supervisorName || !supervisorEmail || !supervisorPhone || !supervisorPosition;
        setSaveEvaluationDisabled(isDisabled);
    }, [serviceSolved, clearArea, questionsAnswered, technicianAttitude, supervisorName, supervisorEmail, supervisorPhone, supervisorPosition]);

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();

        if (!validateField(serviceSolvedDescription, /^.{0,45}$/, "La descripción de si se resolvió el servicio no puede ser mayor a 45 caracteres.")) return;
        if (!validateField(clearAreaDescription, /^.{0,35}$/, "La descripción de si el área quedó limpia no puede ser mayor a 35 caracteres.")) return;
        if (!validateField(questionsAnsweredDescription, /^.{0,30}$/, "La descripción de si las dudas fueron respondidas no puede ser mayor a 30 caracteres.")) return;
        if (!validateField(additionalComment, /^.{0,200}$/, "El comentario adicional o sugerencia no puede ser mayor a 200 caracteres.")) return;
        if (!validateField(supervisorName, /^.{1,30}$/, "El nombre de quien aprueba no puede ser mayor a 30 caracteres ni estar vacío.")) return;
        if (!validateField(supervisorPhone, /^[+]?[0-9]{10,15}$/, "El teléfono principal no es válido. Por favor, ingrese un número válido. Solo acepta números.")) return;
        if (!validateField(supervisorEmail, /^[^\s@]{1,64}@[^\s@]{1,186}\.[^\s@]+$/, "El correo de quién aprueba tiene que tener formato de email.")) return;
        if (!validateField(supervisorPosition, /^.{1,30}$/, "El puesto de quien aprueba no puede ser mayor a 30 caracteres ni estar vacío.")) return;

        setCompleteEvaluationModalVisible(true);
    }

    const resetForm = () => {
        setServiceSolved("")
        setServiceSolvedDescription("")
        setClearArea("")
        setClearAreaDescription("")
        setQuestionsAnswered("")
        setQuestionsAnsweredDescription("")
        setTechnicianAttitude("")
        setAdditionalComment("")
        setSupervisorEmail("")
        setSupervisorPhone("")
        setSupervisorName("")
        setSupervisorPosition("")
    }

    const signDocuments = () => {
        setCompleteEvaluationModalVisible(false);
        setShowSignatureModal(true)
    }

    const validateField = (field: any, regex: any, errorMessage: any) => {
        if (!regex.test(field)) {
            toast.error(errorMessage, {
                position: 'top-right',
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: false,
                theme: 'colored',
                transition: Bounce,
            });
            return false;
        }
        return true;
    };

    const sendCompleteCustomerEvaluation = () => {
        const customerEvaluationDTO = {
            questions: {
                "serviceSolved": serviceSolved,
                "serviceSolvedDescription": serviceSolvedDescription,
                "clearArea": clearArea,
                "clearAreaDescription": clearAreaDescription,
                "questionsAnswered": questionsAnswered,
                "questionsAnsweredDescription": questionsAnsweredDescription,
                "technicianAttitude": technicianAttitude,
                "supervisorEmail": supervisorEmail,
                "supervisorPhone": supervisorPhone,
                "supervisorPosition": supervisorPosition,
                "supervisorName": supervisorName,
            },
            additionalComment: additionalComment
        };

        sendCustomerEvaluation(customerEvaluationDTO, ticketUniqueCode, companySignatureImage, technicianSignatureImage, selectedImages)
            .then(() => {
                toast.success('¡Encuesta guardada exitosamente!', {
                    position: 'top-right',
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: false,
                    theme: 'colored',
                    transition: Bounce,
                });
                updateTicketState("COMPLETED", ticketUniqueCode);
                updateTicketStateLocally("Completado")
            })
            .catch(() => {
                toast.error("Error al guardar encuesta", {
                    position: "top-right",
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: false,
                    theme: "colored",
                    transition: Bounce,
                })
            })
    }

    const openCustomerEvaluation = () => {
        const totalSize = selectedImages.reduce((acc: any, img: any) => acc + img.file.size / (1024 * 1024), 0);
        if (totalSize > MAX_TOTAL_SIZE_MB) {
            toast.error("El peso total de las imágenes no puede ser mayor a 50 MB.", {
                position: 'top-right',
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: false,
                theme: 'colored',
                transition: Bounce,
            });
            return;
        }
        setShowAttachImages(false);
    }

    return (
        <div>
            {sendingEvaluation ? (
                <div className="loading-overlay">
                    <div className="spinner"></div>
                    <div className="loading-message">
                        Guardando...
                    </div>
                </div>
            ) : (
                <div>
                    {completeEvaluationModalVisible && (
                        <div className="modal-overlay">
                            <div className="modal">
                                <p>¿Está seguro que quiere continuar? Ya no podrán ser editados los valores
                                    de la evaluación de los equipos ni de la evaluación del cliente</p>
                                <div className="modal-buttons">
                                    <button onClick={signDocuments}>Aceptar</button>
                                    <button onClick={() => {
                                        setCompleteEvaluationModalVisible(false)
                                    }}>Cancelar
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {showAttachImages ? (
                            <UploadTicketImages onBack={onBack} selectedImages={selectedImages} setSelectedImages={setSelectedImages}
                            goToNextPage={openCustomerEvaluation}/>
                        ) :
                        showSignatureModal ? (
                            <SignatureComponent finishEvaluation={sendCompleteCustomerEvaluation}
                                                companySignatureImage={companySignatureImage}
                                                technicianSignatureImage={technicianSignatureImage}
                                                setCompanySignatureImage={setCompanySignatureImage}
                                                setTechnicianSignatureImage={setTechnicianSignatureImage}/>
                        ) : (
                            <div>
                                <div className="clients-info-view-header">
                                    <button onClick={() => {setShowAttachImages(true)}}>REGRESAR</button>
                                    <div className="clients-info-view-header-title">
                                        <p>CLIENTE</p>
                                    </div>
                                </div>
                                <div className="customer-evaluation-container">
                                    <form className="form-group-sales-rep-tickets" onSubmit={handleSubmit}>
                                        <div className="form-item">
                                            <label htmlFor="cliente">¿Se resolvió el servicio solicitado? *</label>
                                            <select
                                                id="cliente"
                                                value={serviceSolved}
                                                onChange={(e) => setServiceSolved(e.target.value)}
                                            >
                                                <option value=""></option>
                                                <option value="Y">Sí</option>
                                                <option value="N">No</option>
                                            </select>
                                        </div>
                                        <div className="form-item">
                                            <label htmlFor="descripcion">¿Por qué?</label>
                                            <input
                                                id="descripcion"
                                                value={serviceSolvedDescription}
                                                onChange={(e) => setServiceSolvedDescription(e.target.value)}
                                            />
                                        </div>
                                        <div className="form-item">
                                            <label htmlFor="cliente">¿El área quedó con la limpieza y orden correcto?
                                                *</label>
                                            <select
                                                id="cliente"
                                                value={clearArea}
                                                onChange={(e) => setClearArea(e.target.value)}
                                            >
                                                <option value=""></option>
                                                <option value="Y">Sí</option>
                                                <option value="N">No</option>
                                            </select>
                                        </div>
                                        <div className="form-item">
                                            <label htmlFor="descripcion">¿Por qué?</label>
                                            <input
                                                id="descripcion"
                                                value={clearAreaDescription}
                                                onChange={(e) => setClearAreaDescription(e.target.value)}
                                            />
                                        </div>
                                        <div className="form-item">
                                            <label htmlFor="cliente">¿El técnico resolvió sus dudas de acuerdo al
                                                servicio?
                                                *</label>
                                            <select
                                                id="cliente"
                                                value={questionsAnswered}
                                                onChange={(e) => setQuestionsAnswered(e.target.value)}
                                            >
                                                <option value=""></option>
                                                <option value="Y">Sí</option>
                                                <option value="N">No</option>
                                            </select>
                                        </div>
                                        <div className="form-item">
                                            <label htmlFor="descripcion">¿Por qué?</label>
                                            <input
                                                id="descripcion"
                                                value={questionsAnsweredDescription}
                                                onChange={(e) => setQuestionsAnsweredDescription(e.target.value)}
                                            />
                                        </div>
                                        <div className="form-item">
                                            <label htmlFor="cliente">¿Cómo evalua la actitud del técnico? *</label>
                                            <select
                                                id="cliente"
                                                value={technicianAttitude}
                                                onChange={(e) => setTechnicianAttitude(e.target.value)}
                                            >
                                                <option value=""></option>
                                                <option value="EXCELLENT">Excelente</option>
                                                <option value="GOOD">Bien</option>
                                                <option value="REGULAR">Regular</option>
                                                <option value="BAD">Mala</option>
                                            </select>
                                        </div>
                                        <div className="form-item">
                                            <label htmlFor="descripcion">¿Algún comentario adicional o
                                                sugerencia?</label>
                                            <textarea
                                                id="descripcion"
                                                value={additionalComment}
                                                onChange={(e) => setAdditionalComment(e.target.value)}
                                            />
                                        </div>
                                        <div className="form-item">
                                            <label htmlFor="descripcion">Nombre de quien lo aprueba *</label>
                                            <input
                                                id="descripcion"
                                                value={supervisorName}
                                                onChange={(e) => setSupervisorName(e.target.value)}
                                            />
                                        </div>
                                        <div className="form-item">
                                            <label htmlFor="descripcion">Teléfono de quien lo aprueba *</label>
                                            <input
                                                id="descripcion"
                                                value={supervisorPhone}
                                                onChange={(e) => setSupervisorPhone(e.target.value)}
                                            />
                                        </div>
                                        <div className="form-item">
                                            <label htmlFor="descripcion">Email de quien lo aprueba *</label>
                                            <input
                                                id="descripcion"
                                                value={supervisorEmail}
                                                onChange={(e) => setSupervisorEmail(e.target.value)}
                                            />
                                        </div>
                                        <div className="form-item">
                                            <label htmlFor="descripcion">Posición de quien lo aprueba *</label>
                                            <input
                                                id="descripcion"
                                                value={supervisorPosition}
                                                onChange={(e) => setSupervisorPosition(e.target.value)}
                                            />
                                        </div>
                                        <div className="bottom">
                                            <p>* Campos obligatorios</p>
                                            <div className="buttons">
                                                <button type="button" className="cancel" onClick={resetForm}>
                                                    Cancelar
                                                </button>
                                                <button
                                                    type="submit"
                                                    className={`confirm ${saveEvaluationDisabled || sendingEvaluation ? 'disabled' : ''}`}
                                                    disabled={saveEvaluationDisabled || sendingEvaluation}
                                                >
                                                    Confirmar
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}

                </div>
            )}
        </div>
    );
}

export default CustomerEvaluation;